import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import api, { adminApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, Search, Crown, Shield, UserX, Edit, Trash2, Plus,
  ChevronLeft, ChevronRight, Loader2, Mail, Calendar, Key, UserCheck, ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

// Role hierarchy helper
const ROLE_HIERARCHY = { superadmin: 3, admin: 2, user: 1 };
const canManageRole = (actorRole, targetRole) => (ROLE_HIERARCHY[actorRole] || 0) > (ROLE_HIERARCHY[targetRole] || 0);
const canAssignRole = (actorRole, newRole) => {
  if (actorRole === 'superadmin') return ['user', 'admin', 'superadmin'].includes(newRole);
  if (actorRole === 'admin') return newRole === 'user';
  return false;
};

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]); // For approver dropdown
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Create user modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createData, setCreateData] = useState({
    email: '', password: '', name: '', role: 'user', tier: 'free', approver_id: ''
  });
  const [creating, setCreating] = useState(false);
  
  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editData, setEditData] = useState({ name: '', role: '', tier: '', is_active: true, approver_id: '' });
  
  // Password reset modal
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  
  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [ownershipSummary, setOwnershipSummary] = useState(null);
  const [loadingOwnership, setLoadingOwnership] = useState(false);
  const [transferOwnership, setTransferOwnership] = useState(false);
  const [transferToUserId, setTransferToUserId] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Get available role options based on current user's role
  const getAvailableRoles = () => {
    if (currentUser?.role === 'superadmin') {
      return [
        { value: 'user', label: 'User' },
        { value: 'admin', label: 'Admin' },
        { value: 'superadmin', label: 'Super Admin' }
      ];
    }
    // Admin can only create/assign user role
    return [{ value: 'user', label: 'User' }];
  };

  // Check if current user can edit the target user
  const canEditUser = (targetUser) => {
    if (!currentUser || !targetUser) return false;
    return canManageRole(currentUser.role, targetUser.role);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (searchQuery) params.search = searchQuery;
      if (tierFilter !== 'all') params.tier = tierFilter;
      if (roleFilter !== 'all') params.role = roleFilter;
      
      const response = await adminApi.getUsers(params);
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, tierFilter, roleFilter]);

  const fetchAllUsers = async () => {
    try {
      const response = await adminApi.getUsers({ limit: 500 });
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch all users');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAllUsers();
  }, [fetchUsers]);

  const handleSearch = () => {
    setPage(1);
    fetchUsers();
  };

  // Create User
  const handleCreateUser = async () => {
    if (!createData.email || !createData.password || !createData.name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreating(true);
    try {
      await api.post('/admin/users', {
        ...createData,
        approver_id: createData.approver_id || null
      });
      toast.success('User created successfully');
      setCreateModalOpen(false);
      setCreateData({ email: '', password: '', name: '', role: 'user', tier: 'free', approver_id: '' });
      fetchUsers();
      fetchAllUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  // Edit User
  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditData({
      name: user.name || '',
      role: user.role || 'user',
      tier: user.tier || 'free',
      is_active: user.is_active !== false,
      approver_id: user.approver_id || ''
    });
    setEditModalOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      // Update basic info
      await adminApi.updateUser(selectedUser.user_id, {
        name: editData.name,
        role: editData.role,
        tier: editData.tier,
        is_active: editData.is_active,
        approver_id: editData.approver_id || null
      });
      toast.success('User updated successfully');
      setEditModalOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  // Password Reset
  const openPasswordModal = (user) => {
    setPasswordUser(user);
    setNewPassword('');
    setPasswordModalOpen(true);
  };

  const handleResetPassword = async () => {
    if (!passwordUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    setResettingPassword(true);
    try {
      await api.post(`/admin/users/${passwordUser.user_id}/reset-password`, {
        new_password: newPassword
      });
      toast.success('Password reset successfully');
      setPasswordModalOpen(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Delete User
  const openDeleteModal = async (user) => {
    setUserToDelete(user);
    setTransferOwnership(false);
    setTransferToUserId('');
    setOwnershipSummary(null);
    setDeleteModalOpen(true);
    
    // Fetch ownership summary
    setLoadingOwnership(true);
    try {
      const response = await api.get(`/admin/users/${user.user_id}/ownership-summary`);
      setOwnershipSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch ownership summary:', error);
    } finally {
      setLoadingOwnership(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    // If transfer is selected but no user chosen
    if (transferOwnership && !transferToUserId) {
      toast.error('Please select a user to transfer ownership to');
      return;
    }
    
    setDeleting(true);
    try {
      const params = transferOwnership ? `?transfer_to=${transferToUserId}` : '';
      await api.delete(`/admin/users/${userToDelete.user_id}${params}`);
      toast.success(transferOwnership ? 'User deleted and ownership transferred' : 'User deleted successfully');
      setDeleteModalOpen(false);
      setUserToDelete(null);
      setOwnershipSummary(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  // Get eligible transfer targets based on user being deleted
  const getTransferTargets = () => {
    if (!userToDelete) return [];
    const targetRole = userToDelete.role;
    
    return allUsers.filter(u => {
      // Can't transfer to self or the user being deleted
      if (u.user_id === userToDelete.user_id) return false;
      if (u.user_id === currentUser?.user_id) return false;
      
      // If deleting admin/superadmin, can only transfer to admin/superadmin
      if (targetRole === 'admin' || targetRole === 'superadmin') {
        return u.role === 'admin' || u.role === 'superadmin';
      }
      
      // For normal users, can transfer to anyone
      return true;
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const getApproverName = (approverId) => {
    if (!approverId) return '-';
    const approver = allUsers.find(u => u.user_id === approverId);
    return approver?.name || '-';
  };

  return (
    <AdminLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="admin-users-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope' }}>
              User Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400">Manage {total} users</p>
          </div>
          <Button 
            onClick={() => setCreateModalOpen(true)}
            className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
            data-testid="create-user-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create User
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-10 rounded-xl"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Select value={tierFilter} onValueChange={(v) => { setTierFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[140px] rounded-xl">
                  <SelectValue placeholder="Tier" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={(v) => { setRoleFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[140px] rounded-xl">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="superadmin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSearch} variant="outline" className="rounded-xl">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#4A9CC7]" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No users found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-slate-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">User</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Tier</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Approver</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Joined</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                    {users.map((user) => (
                      <tr key={user.user_id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50" data-testid={`user-row-${user.user_id}`}>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#4A9CC7]/10 flex items-center justify-center">
                              <span className="text-[#4A9CC7] font-medium">{user.name?.charAt(0) || 'U'}</span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">{user.name}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Badge 
                            variant={user.role === 'superadmin' ? 'destructive' : user.role === 'admin' ? 'default' : 'secondary'} 
                            className="flex items-center gap-1 w-fit"
                          >
                            {user.role === 'superadmin' ? <ShieldAlert className="w-3 h-3" /> : 
                             user.role === 'admin' ? <Shield className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                            {user.role === 'superadmin' ? 'Super Admin' : user.role}
                          </Badge>
                        </td>
                        <td className="px-4 py-4">
                          <Badge variant={user.tier === 'premium' ? 'default' : 'outline'} className="flex items-center gap-1 w-fit">
                            {user.tier === 'premium' && <Crown className="w-3 h-3" />}
                            {user.tier}
                          </Badge>
                        </td>
                        <td className="px-4 py-4">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {getApproverName(user.approver_id)}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <Badge variant={user.is_active !== false ? 'success' : 'destructive'}>
                            {user.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(user.created_at)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openPasswordModal(user)} title="Reset Password" disabled={!canEditUser(user)}>
                              <Key className={`w-4 h-4 ${canEditUser(user) ? 'text-gray-500' : 'text-gray-300'}`} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditModal(user)} title="Edit User" disabled={!canEditUser(user)}>
                              <Edit className={`w-4 h-4 ${canEditUser(user) ? 'text-gray-500' : 'text-gray-300'}`} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openDeleteModal(user)} title="Delete User" disabled={!canEditUser(user)}>
                              <Trash2 className={`w-4 h-4 ${canEditUser(user) ? 'text-red-500' : 'text-gray-300'}`} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t dark:border-slate-700">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create User Modal */}
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#4A9CC7]" />
                Create New User
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={createData.name}
                  onChange={(e) => setCreateData({ ...createData, name: e.target.value })}
                  placeholder="Full name"
                  className="mt-1"
                  data-testid="create-user-name"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={createData.email}
                  onChange={(e) => setCreateData({ ...createData, email: e.target.value })}
                  placeholder="email@example.com"
                  className="mt-1"
                  data-testid="create-user-email"
                />
              </div>
              <div>
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={createData.password}
                  onChange={(e) => setCreateData({ ...createData, password: e.target.value })}
                  placeholder="Set initial password"
                  className="mt-1"
                  data-testid="create-user-password"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Role</Label>
                  <Select value={createData.role} onValueChange={(v) => setCreateData({ ...createData, role: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableRoles().map(role => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tier</Label>
                  <Select value={createData.tier} onValueChange={(v) => setCreateData({ ...createData, tier: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Approver</Label>
                <Select value={createData.approver_id || 'none'} onValueChange={(v) => setCreateData({ ...createData, approver_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select approver" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Approver</SelectItem>
                    {allUsers.map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateUser} disabled={creating} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]">
                {creating ? 'Creating...' : 'Create User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5 text-[#4A9CC7]" />
                Edit User: {selectedUser?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Role</Label>
                  <Select value={editData.role} onValueChange={(v) => setEditData({ ...editData, role: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {getAvailableRoles().map(role => (
                        <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tier</Label>
                  <Select value={editData.tier} onValueChange={(v) => setEditData({ ...editData, tier: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="premium">Premium</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Approver</Label>
                <Select 
                  value={editData.approver_id || 'none'} 
                  onValueChange={(v) => setEditData({ ...editData, approver_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select approver" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Approver</SelectItem>
                    {allUsers.filter(u => u.user_id !== selectedUser?.user_id).map(u => (
                      <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select 
                  value={editData.is_active ? 'active' : 'inactive'} 
                  onValueChange={(v) => setEditData({ ...editData, is_active: v === 'active' })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModalOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateUser} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Password Reset Modal */}
        <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="w-5 h-5 text-[#4A9CC7]" />
                Reset Password: {passwordUser?.name}
              </DialogTitle>
              <DialogDescription>
                Set a new password for this user. They will need to use this password to log in.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="mt-1"
                data-testid="new-password-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPasswordModalOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleResetPassword} 
                disabled={resettingPassword || !newPassword}
                className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
              >
                {resettingPassword ? 'Resetting...' : 'Reset Password'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal with Transfer Option */}
        <Dialog open={deleteModalOpen} onOpenChange={(open) => {
          if (!open) {
            setDeleteModalOpen(false);
            setUserToDelete(null);
            setOwnershipSummary(null);
            setTransferOwnership(false);
            setTransferToUserId('');
          }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Delete User: {userToDelete?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Ownership Summary */}
              {loadingOwnership ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">Loading user data...</span>
                </div>
              ) : ownershipSummary?.has_data ? (
                <div className="space-y-3">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-medium text-amber-800 mb-2">
                      This user owns the following data:
                    </p>
                    <ul className="text-sm text-amber-700 space-y-1">
                      {ownershipSummary.ownership.workspaces > 0 && (
                        <li>• {ownershipSummary.ownership.workspaces} workspace(s)</li>
                      )}
                      {ownershipSummary.ownership.tasks > 0 && (
                        <li>• {ownershipSummary.ownership.tasks} task(s)</li>
                      )}
                      {ownershipSummary.ownership.forms > 0 && (
                        <li>• {ownershipSummary.ownership.forms} form(s)</li>
                      )}
                      {ownershipSummary.ownership.launchpad_folders > 0 && (
                        <li>• {ownershipSummary.ownership.launchpad_folders} launchpad folder(s)</li>
                      )}
                      {ownershipSummary.ownership.assigned_tasks > 0 && (
                        <li>• {ownershipSummary.ownership.assigned_tasks} assigned task(s) (will be unassigned)</li>
                      )}
                    </ul>
                  </div>

                  {/* Transfer Option */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">What would you like to do with this data?</Label>
                    
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50 dark:hover:bg-slate-800">
                        <input
                          type="radio"
                          name="deleteOption"
                          checked={!transferOwnership}
                          onChange={() => setTransferOwnership(false)}
                          className="w-4 h-4 text-red-600"
                        />
                        <div>
                          <span className="text-sm font-medium text-red-600">Delete all data</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Permanently remove all workspaces, tasks, forms, and other data</p>
                        </div>
                      </label>
                      
                      <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50 dark:hover:bg-slate-800">
                        <input
                          type="radio"
                          name="deleteOption"
                          checked={transferOwnership}
                          onChange={() => setTransferOwnership(true)}
                          className="w-4 h-4 text-blue-600"
                        />
                        <div>
                          <span className="text-sm font-medium text-blue-600">Transfer ownership</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Transfer all data to another user before deleting</p>
                        </div>
                      </label>
                    </div>

                    {/* Transfer Target Selection */}
                    {transferOwnership && (
                      <div className="space-y-2 mt-3">
                        <Label className="text-sm">
                          Transfer to {(userToDelete?.role === 'admin' || userToDelete?.role === 'superadmin') 
                            ? '(Admin/Super Admin only)' 
                            : ''}
                        </Label>
                        <Select value={transferToUserId} onValueChange={setTransferToUserId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select user to transfer to..." />
                          </SelectTrigger>
                          <SelectContent>
                            {getTransferTargets().map(u => (
                              <SelectItem key={u.user_id} value={u.user_id}>
                                <div className="flex items-center gap-2">
                                  <span>{u.name}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {u.role}
                                  </Badge>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {getTransferTargets().length === 0 && (
                          <p className="text-xs text-red-500">
                            No eligible users to transfer to. 
                            {(userToDelete?.role === 'admin' || userToDelete?.role === 'superadmin') && 
                              ' Admin data must be transferred to another admin or super admin.'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Are you sure you want to delete <strong>{userToDelete?.name}</strong>? 
                  This user has no owned data. The account will be permanently removed.
                </p>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteUser}
                disabled={deleting || (transferOwnership && !transferToUserId)}
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : transferOwnership ? (
                  'Transfer & Delete'
                ) : (
                  'Delete User'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
